import React, { useCallback } from 'react';

import { FormattedMessage } from 'react-intl-next';

import {
	AnalyticsEventActions,
	AnalyticsEventSource,
	useAnalytics,
	useDialog,
} from '@atlassian/ptc-common';
import { useSettings, useTeamVisibility } from '@atlassian/teams';

import TeamCreateDialogLazy from '../TeamCreateDialog/lazy';
import { type UserItemType } from '../TeamInviteDialog/types';

import { messages } from './messages';
import { TeamAddIcon } from './TeamAddIcon';
import * as Styled from './TeamsProfileItem.styled';

export interface TeamsProfileItemButtonProps {
	/**
	 * The members to auto-add when the create team button is clicked
	 */
	proposedMembers?: UserItemType[];
	/**
	 * Analytics source. Tells the analytics event which screen the button was
	 * clicked from
	 */
	source: string;
	isCurrentUser: boolean;
	isActiveUser: boolean;
}

const TeamsProfileItemButton = (props: TeamsProfileItemButtonProps) => {
	const analytics = useAnalytics();
	const { openDialog } = useDialog();
	const { proposedMembers, source, isCurrentUser, isActiveUser } = props;
	const {
		permissions: { canCreateTeams },
	} = useTeamVisibility();
	const [{ hasBrowseUsersPermission }] = useSettings();

	const handleClick = useCallback(() => {
		// Track where trigger button is being clicked from
		analytics.pushUIEvent({
			source: AnalyticsEventSource.CREATE_TEAM_MODAL,
			action: AnalyticsEventActions.CLICKED,
			actionSubject: 'teamCreateDialogTriggerButton',
			attributes: {
				// Record where in the UI the modal was triggered from
				trigger: source,
			},
		});

		// @ts-ignore
		openDialog(TeamCreateDialogLazy, {
			proposedMembers: proposedMembers || [],
		});
	}, [analytics, openDialog, proposedMembers, source]);

	const canStartTeamWithUser = hasBrowseUsersPermission && isActiveUser && canCreateTeams;

	/**
	 * Show the button if:
	 * - it's the current user's profile page
	 * - or it's other people's profile pages and the user has permission
	 */
	const showButton = isCurrentUser || (!isCurrentUser && canStartTeamWithUser);

	return showButton ? (
		<Styled.OutlineLink onClick={handleClick}>
			<Styled.TeamsProfileItemWrapper>
				<Styled.TeamsProfileAddIcon>
					<TeamAddIcon />
				</Styled.TeamsProfileAddIcon>
				<Styled.TeamsProfileItemBody>
					<Styled.TeamsProfileItemTeamName>
						{isCurrentUser ? (
							<FormattedMessage {...messages.createTeamLabel} />
						) : (
							<FormattedMessage {...messages.createTeamLabelOtherPeople} />
						)}
					</Styled.TeamsProfileItemTeamName>
				</Styled.TeamsProfileItemBody>
			</Styled.TeamsProfileItemWrapper>
		</Styled.OutlineLink>
	) : null;
};

export default TeamsProfileItemButton;
